import { Space, Loader } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useUserRole } from '@ospace/auth'
import { useClientCampaign } from '@ospace/campaign'
import { isClientsLeadsDashboardAvailableToUserRole } from '@ospace/core-auth'
import { SelectedPage, useNavLayout } from '@ospace/layout'
import { ErrorBoundary } from '@ospace/shared'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useDataDogEmailTracker } from '../../datadog'
import { LeadsBarChart } from '../components/LeadsBarChart'
import { LeadsTable } from '../components/LeadsTable/LeadsTable'
import { LeadsWidgets } from '../components/LeadsWidgets/LeadsWidgets'
import { useLeads } from '../hooks/useLeadsDashBoard'
import { useLeadsStats } from '../hooks/useLeadsStats'

const PAGE_LIMIT = 15

export const LeadsDashBoardPage = ({ clientId }: { clientId: number }) => {
  const { client, duration, setLayoutConfig } = useNavLayout()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchInput, 400)
  const userRole = useUserRole()
  const navigate = useNavigate()

  useDataDogEmailTracker()

  useEffect(() => {
    setLayoutConfig({ pageId: 'leads', pageTitle: 'Leads' })
  }, [setLayoutConfig])

  useEffect(() => {
    if (!isClientsLeadsDashboardAvailableToUserRole({ userRole, client })) {
      navigate('/error/403', { replace: true })
    }
  }, [userRole, client, navigate])

  const campaignsResponse = useClientCampaign(clientId)
  const currency = client.currency || 'USD'
  const campaigns = campaignsResponse?.data || []

  const statsQuery = useLeadsStats({ clientId, duration })
  const leadsQuery = useLeads({
    clientId,
    page,
    limit: PAGE_LIMIT,
    duration,
    search: debouncedSearch,
  })

  const totalPages = useMemo(() => {
    if (!leadsQuery.data?.count) return 1
    return Math.ceil(leadsQuery.data.count / PAGE_LIMIT)
  }, [leadsQuery.data?.count])

  const isLoading =
    leadsQuery.status === 'loading' || statsQuery.status === 'loading' || !statsQuery.data

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <SelectedPage>Leads</SelectedPage>
      <LeadsWidgets stats={statsQuery.data} duration={duration} />
      <Space h='md' />
      <LeadsBarChart stats={statsQuery.data} />
      <Space h='md' />
      <LeadsTable
        leads={leadsQuery.data?.data || []}
        campaigns={campaigns}
        currency={currency}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        search={searchInput}
        loading={leadsQuery.isFetching}
        onSearchChange={(value) => {
          setPage(1)
          setSearchInput(value)
        }}
      />
    </>
  )
}

const Controller = () => {
  const params = useParams()
  const clientId = Number(params?.clientId)
  return <LeadsDashBoardPage clientId={clientId} />
}

export const LeadsDashBoardPageHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
